import { useCallback, useEffect, useState } from "react";
import { Search } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { STATUSES, statusOf } from "../lib/constants";
import { SubmissionDetail } from "./SubmissionDetail";

export const StatusBadge = ({ value }) => {
  const s = statusOf(value);
  return <span className={`inline-block rounded-full px-2.5 py-0.5 text-xs font-bold ${s.color}`}>{s.label}</span>;
};

const Stat = ({ label, value, accent, testId }) => (
  <div className="panel p-5" data-testid={testId}>
    <p className="text-xs font-semibold uppercase tracking-widest text-slate-500">{label}</p>
    <p className={`mt-1 font-display text-4xl font-black ${accent || ""}`}>{value ?? "–"}</p>
  </div>
);

export default function Submissions() {
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState(null);
  const [filters, setFilters] = useState({ type: "", status: "", q: "" });
  const [openId, setOpenId] = useState(null);

  const load = useCallback(async () => {
    try {
      const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
      const [a, b] = await Promise.all([api.get("/admin/submissions", { params }), api.get("/admin/stats")]);
      setRows(a.data);
      setStats(b.data);
    } catch (e) { toast.error(errMsg(e)); }
  }, [filters]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const inProgress = stats ? ["reviewing", "invoice_sent", "paid", "in_production"].reduce((n, k) => n + stats.by_status[k], 0) : null;
  return (
    <div>
      <h1 className="font-display text-4xl font-black uppercase">Orders & Quotes</h1>
      <div className="mt-6 grid grid-cols-2 gap-4 md:grid-cols-4">
        <Stat label="New" value={stats?.by_status.new} accent="text-[#E5202A]" testId="admin-stat-new" />
        <Stat label="In progress" value={inProgress} accent="text-[#60A5FA]" testId="admin-stat-progress" />
        <Stat label="Ready for pickup" value={stats?.by_status.ready} accent="text-violet-400" testId="admin-stat-ready" />
        <Stat label="All time" value={stats?.total} testId="admin-stat-total" />
      </div>
      <div className="mt-6 flex flex-wrap gap-3">
        <div className="relative min-w-[220px] flex-1">
          <Search className="absolute left-3 top-3.5 h-4 w-4 text-slate-500" />
          <input className="field pl-9" placeholder="Search name, email, phone or #" value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} data-testid="admin-search-input" />
        </div>
        <select className="field w-auto" value={filters.type} onChange={(e) => setFilters({ ...filters, type: e.target.value })} data-testid="admin-type-filter">
          <option value="">Orders + Quotes</option><option value="order">Orders</option><option value="quote">Quotes</option>
        </select>
        <select className="field w-auto" value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })} data-testid="admin-status-filter">
          <option value="">All statuses</option>
          {STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
      </div>
      <div className="panel mt-4 overflow-x-auto">
        <table className="w-full min-w-[720px] text-left text-sm" data-testid="admin-submissions-table">
          <thead className="text-xs uppercase tracking-wider text-slate-500"><tr>{["#", "Type", "Customer", "Summary", "Needed by", "Received", "Status"].map((h) => <th key={h} className="px-4 py-3">{h}</th>)}</tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} onClick={() => setOpenId(r.id)} className="cursor-pointer border-t border-white/5 transition-colors hover:bg-white/5" data-testid={`admin-submission-row-${r.number}`}>
                <td className="px-4 py-3 font-mono-label text-xs">{r.number}</td>
                <td className="px-4 py-3 capitalize">{r.type}</td>
                <td className="px-4 py-3"><p className="font-semibold">{r.customer.name}</p><p className="text-xs text-slate-500">{r.customer.email}</p></td>
                <td className="px-4 py-3 text-slate-400">{r.type === "order" ? r.items.map((i) => `${i.item_type} ×${i.quantity}`).join(", ") : r.service}</td>
                <td className="px-4 py-3 text-slate-400">{r.needed_by || "–"}</td>
                <td className="px-4 py-3 text-slate-400">{new Date(r.created_at).toLocaleDateString()}</td>
                <td className="px-4 py-3"><StatusBadge value={r.status} /></td>
              </tr>
            ))}
            {!rows.length && <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-500" data-testid="admin-submissions-empty">No orders or quotes yet.</td></tr>}
          </tbody>
        </table>
      </div>
      <SubmissionDetail id={openId} onClose={() => setOpenId(null)} onChanged={load} />
    </div>
  );
}
